/**
 * **climax テーマ**（Issue #543。索引 #539）。実写デュエルを再現したあと、
 * **アニメやゲームのように格好よく迫力のある絵**で見せるための舞台。
 *
 * - 盤: 刻印の入ったガンメタルのマットの絵・ゾーンの種類ごとの鋼の枠の絵（`drawField.js`。画像は `field/`。Issue #565）
 * - 舞台の背景: `field/stage_*.webp` から選ぶ（既定は B＝燃える空と壊れた塔。`stageBg`）
 * - 置かれているもの: 色の縁のカード（ATK / DEF の帯）・斜めの枚数の札・光る盤の縁・照準・チェーンの札（`drawObjects.js`）
 * - HUD: 上の左右の斜めの LP ゲージ、中央のターンの紋章。演出の作用（`fx`）で揺れる・光る（`drawHud.js`）
 * - 演出: `effects/<event>.js`（1 ファイル = 1 イベント。three で組む `script` 層が主）
 *
 * **arcade は契約の確認用**で、ここはそれを真似しない（ユーザー判断 2026-09-25）。
 *
 * **見た目の決まり（何を格好いいとするか）は `THEME.md` と `look/` の絵が一次情報**。作業の前に読む。
 */
import { toHud } from '../../core/hud';
import { toTheme } from '../../core/theme';
import { EFFECTS } from './effects';
import field, { FIELD_PARTS, FIELD_SCHEMA, FIELD_STYLE, stageBackground } from './drawField';
import { FIELD_IMAGES } from './field/assets';
import objects, { MARK_PARTS, MARK_SCHEMA, MARK_STYLE, OBJECT_PARTS, OBJECT_SCHEMA, OBJECT_STYLE } from './drawObjects';
import ClimaxHud, { HUD_PARTS, HUD_SCHEMA, HUD_STYLE, anchors } from './drawHud';

/**
 * **後処理のノブ**（Issue #549）。演出の層の光のにじみ（ブルーム）と色収差。
 * 演出は `scene.grade` の `bloom` / `chroma` で**一瞬だけ足せる**（インパクトの瞬間など）。
 */
const POST_SCHEMA = Object.freeze({
  postBloom: { type: 'number', label: 'ブルームの強さ', min: 0, max: 3, step: 0.05, part: 'post' },
  postThreshold: { type: 'number', label: 'ブルームのしきい値', min: 0, max: 1, step: 0.01, part: 'post' },
  postRadius: { type: 'number', label: 'ブルームの広さ', min: 0.5, max: 4, step: 0.1, part: 'post' },
  postChroma: { type: 'number', label: '色収差', min: 0, max: 0.02, step: 0.0005, part: 'post' },
});
/** ブルームは切ってある（もやもやした光は使わない。THEME.md。ユーザー 2026-09-26）。ノブは残す */
const POST_STYLE = Object.freeze({ postBloom: 0, postThreshold: 0.55, postRadius: 1.6, postChroma: 0.0015 });

export default toTheme({
  name: 'climax',
  label: 'クライマックス（アニメ×ゲーム）',
  field,
  objects,
  effects: EFFECTS,
  schema: { ...HUD_SCHEMA, ...FIELD_SCHEMA, ...OBJECT_SCHEMA, ...MARK_SCHEMA, ...POST_SCHEMA },
  style: { ...HUD_STYLE, ...FIELD_STYLE, ...OBJECT_STYLE, ...MARK_STYLE, ...POST_STYLE },
  parts: [...HUD_PARTS, ...FIELD_PARTS, ...OBJECT_PARTS, ...MARK_PARTS, { part: 'post', label: '後処理（ブルーム・色収差）', space: 'screen' }],
  post: (style) => ({ bloom: style.postBloom, threshold: style.postThreshold, radius: style.postRadius, chroma: style.postChroma }),
  // **舞台の背景と盤の画像**（Issue #565）
  background: stageBackground,
  images: FIELD_IMAGES,
  hud: toHud({ anchors, Component: ClimaxHud, options: {} }),
  options: {
    zoneLabels: true,
  },
});
